import { api } from './api';

// Types for leads
export type LeadStatus = 'new' | 'contacted' | 'qualified' | 'converted' | 'lost';
export type LeadSource = 'landing_page' | 'manual' | 'referral' | 'other';

export interface Lead {
  _id: string;
  organizationId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  company?: string;
  challengeText: string;
  strategyPlan?: string;
  status: LeadStatus;
  source: LeadSource;
  notes?: string;
  convertedToClientId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface LeadsResponse {
  leads: Lead[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface LeadFilters {
  status?: LeadStatus;
  source?: LeadSource;
  search?: string;
  page?: number;
  limit?: number;
}

export interface CreateLeadPayload {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  company?: string;
  challengeText?: string;
  source?: LeadSource;
  notes?: string;
}

export interface UpdateLeadPayload {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  company?: string;
  status?: LeadStatus;
  notes?: string;
}

export const leadsApi = {
  /**
   * Get all leads with optional filters
   */
  getLeads: async (filters: LeadFilters = {}): Promise<LeadsResponse> => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.source) params.append('source', filters.source);
    if (filters.search) params.append('search', filters.search);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());

    const { data } = await api.get(`/leads?${params.toString()}`);
    return data.data;
  },

  /**
   * Get a single lead by ID
   */
  getLead: async (id: string): Promise<Lead> => {
    const { data } = await api.get(`/leads/${id}`);
    return data.data;
  },

  /**
   * Create a new lead manually
   */
  createLead: async (payload: CreateLeadPayload): Promise<Lead> => {
    const { data } = await api.post('/leads', payload);
    return data.data;
  },

  /**
   * Update a lead
   */
  updateLead: async (id: string, payload: UpdateLeadPayload): Promise<Lead> => {
    const { data } = await api.patch(`/leads/${id}`, payload);
    return data.data;
  },

  /**
   * Delete a lead
   */
  deleteLead: async (id: string): Promise<void> => {
    await api.delete(`/leads/${id}`);
  },

  /**
   * Convert a lead to a client
   */
  convertToClient: async (id: string): Promise<{ lead: Lead; client: { _id: string } }> => {
    const { data } = await api.post(`/leads/${id}/convert`);
    return data.data;
  },
};
